import type { FieldMissionItem } from '@groupe-reca/contracts'
import { useEffect, useState } from 'react'
import type { Radii } from '@/features/detection/domain/geometry'
import { describeEvent, formatDuration } from '@/features/detection/presentation/describeEvent'
import type { Detection } from '../../application/useDetection'
import type { DetectionDiagnosis } from '../../application/detectionSource'
import type { TrackerState } from '../../application/tracking/PositionTracker'
import { itemAddress } from '../../domain/fieldText'
import { ruleInfo } from '@/features/detection/domain/rules'
import { debugRows, headingDebug } from '../detectionDebug'
import type { StableMap } from '../useStableMap'
import type { RotationMode } from './MissionMap'
import { FloatingWindow } from './FloatingWindow'

// Panneau de calibration TEMPORAIRE (hors production) : état du moteur, GPS, distances aux drapeaux. À retirer après
// l'observation terrain (lot O7).

const diagnosisText: Record<DetectionDiagnosis['status'], string> = {
  READY: 'active',
  NO_PROFILE: 'INACTIVE — aucun profil figé dans la mission (publier un profil, puis redémarrer une mission)',
  OFF: 'INACTIVE — profil en mode OFF',
  INVALID: 'INACTIVE — profil invalide',
}

const ringText = { CAPTURE: 'capture', WORK: 'travail', BAND: 'bande', OUTSIDE: 'dehors' } as const

const meters = (value: number | null) => (value === null ? '—' : `${value.toFixed(1)} m`)

interface Props {
  items: readonly FieldMissionItem[]
  diagnosis: DetectionDiagnosis
  detection: Detection | null
  gps: TrackerState
  /** Rayons tracés : ceux du profil, sinon la référence Démo v0.3 (signalée). */
  radii: Radii
  radiiLabel: string
  /** Carte stable et rotation appliquée : ligne « Cap ». */
  stable: StableMap
  rotation: { rotation: RotationMode; following: boolean } | null
  onClose: () => void
}

export function DetectionDebugPanel({
  items,
  diagnosis,
  detection,
  gps,
  radii,
  radiiLabel,
  stable,
  rotation,
  onClose,
}: Props) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now())
    }, 1000)
    return () => {
      window.clearInterval(id)
    }
  }, [])

  const snapshot = detection?.snapshot ?? null
  const profile = detection?.setup.profile ?? null
  const fix = snapshot?.lastFix ?? null
  const position = fix?.point ?? gps.position
  const engine = snapshot
    ? { statuses: snapshot.view.statuses, hits: snapshot.debug.hits, disarmed: snapshot.debug.disarmed }
    : null
  const rows = debugRows(items, position, radii, engine).slice(0, 6)
  const labelOf = (itemId: string) => {
    const item = items.find((candidate) => candidate.id === itemId)
    return item ? itemAddress(item.snapshot) : itemId.slice(0, 8)
  }
  const active = snapshot?.view.active ?? null
  const fixAt = fix?.at ?? gps.lastFixAt
  const cap = headingDebug({
    source: stable.camera.headingSource,
    mode: stable.camera.mode,
    heading: stable.camera.heading,
    deviceHeading: gps.heading,
    direction: stable.direction,
    pathOffsetM: stable.pathOffsetM,
    snapToleranceM: stable.snapToleranceM,
    rotation: rotation?.rotation ?? null,
    following: rotation?.following ?? null,
  })

  return (
    <FloatingWindow
      title="DIAGNOSTIC (temporaire)"
      label="Diagnostic de détection"
      storageKey="reca-operator-v3.debug-window"
      initial={{ x: window.innerWidth - 580, y: 72 }}
      width={560}
      className="op-debug"
      onClose={onClose}
    >
      <div>
        Détection :{' '}
        <b>
          {detection?.overridden
            ? `active — profil modifié sur cette tablette (serveur : ${diagnosisText[diagnosis.status]})`
            : diagnosisText[diagnosis.status]}
        </b>
      </div>
      {detection && ruleInfo.some((rule) => !detection.rules[rule.id]) ? (
        <div className="op-debug__warn">
          Règles coupées : {ruleInfo.filter((rule) => !detection.rules[rule.id]).map((rule) => rule.label).join(', ')}
        </div>
      ) : null}
      {diagnosis.status === 'INVALID' ? <div className="op-debug__warn">{diagnosis.issues.join(' · ')}</div> : null}
      <div>
        Drapeaux : {diagnosis.withFlag}/{diagnosis.total}
        {diagnosis.withFlag === 0 ? <b className="op-debug__warn"> — aucun drapeau : rien à détecter</b> : null}
      </div>
      {profile ? (
        <div>
          Profil {profile.label} v{profile.version} · {profile.mode} · capture {profile.captureRadiusM} / travail{' '}
          {profile.workRadiusM} / sortie {profile.exitDistanceM} m · {profile.captureConfirmCount} pos. sur{' '}
          {profile.captureConfirmWindowS} s · précision ≤ {profile.maxAccuracyM} m · âge ≤ {profile.maxFixAgeS} s
        </div>
      ) : (
        <div>Cercles tracés : {radiiLabel}</div>
      )}
      <div>
        GPS tablette : {gps.status}
        {position ? ` · ${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}` : ' · aucune position'}
        {fix ? ` · précision ${fix.accuracyM === null ? '?' : `${fix.accuracyM.toFixed(0)} m`}` : ''}
        {fixAt ? ` · il y a ${String(Math.max(0, Math.round((now - fixAt) / 1000)))} s` : ''}
      </div>
      <div>
        Cap : <b>{cap.source}</b> · carte {cap.mapHeading} · appareil {cap.deviceHeading} (écart {cap.gap}) · {cap.path} ·{' '}
        {cap.direction} · rotation {cap.rotation}
        {cap.warning ? <b className="op-debug__warn"> — {cap.warning}</b> : null}
      </div>
      {snapshot ? (
        <div>
          Moteur : GPS {snapshot.view.gps.status}
          {snapshot.view.gps.reason ? ` (${snapshot.view.gps.reason})` : ''} · {snapshot.debug.fixCount} positions reçues
          {snapshot.view.paused ? ' · EN PAUSE' : ''}
          {snapshot.view.ambiguity ? ` · ambiguïté : ${snapshot.view.ambiguity.map(labelOf).join(', ')}` : ''}
        </div>
      ) : null}
      {active ? (
        <div>
          Active : <b>{labelOf(active.itemId)}</b> · {ringText[active.ring]} · observé {formatDuration(active.observedMs)}{' '}
          · en mouvement {formatDuration(active.movingMs)} · {active.admissible ? 'admissible' : 'pas encore admissible'}
          {active.exitCandidate ? ' · SORTIE CANDIDATE' : ''}
        </div>
      ) : null}
      <table className="op-debug__table">
        <thead>
          <tr>
            <th>Adresse</th>
            <th>→ drapeau</th>
            <th>→ pastille</th>
            <th>écart</th>
            <th>anneau</th>
            <th>état</th>
            <th>pos.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.itemId} className={row.ring === 'CAPTURE' ? 'op-debug__hot' : undefined}>
              <td>{row.address}</td>
              <td>{row.flag ? meters(row.flagDistanceM) : 'sans drapeau'}</td>
              <td>{meters(row.markerDistanceM)}</td>
              <td>{meters(row.flagOffsetM)}</td>
              <td>{row.ring ? ringText[row.ring] : '—'}</td>
              <td>
                {row.status ?? '—'}
                {row.disarmed ? ' (désarmée)' : ''}
              </td>
              <td>
                {row.hits}
                {profile ? `/${String(profile.captureConfirmCount)}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {snapshot && profile && snapshot.decisions.length > 0 ? (
        <ul className="op-debug__events">
          {snapshot.decisions
            .slice(-4)
            .reverse()
            .map((event, index) => {
              const text = describeEvent(event, labelOf, profile, profile.mode !== 'AUTO')
              return (
                <li key={`${String(event.at)}-${String(index)}`}>
                  {new Date(event.at).toLocaleTimeString('fr-CA')} · {text.decision} — {text.motive}
                </li>
              )
            })}
        </ul>
      ) : null}
    </FloatingWindow>
  )
}
